import React, { useState, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { signs, Sign } from '../../data/signs';
import { ParticleField } from '../shared/ParticleField';
export function SignGrid() {
  const navigate = useNavigate();
  const [selectedSign, setSelectedSign] = useState<Sign | null>(null);
  const [activeCard, setActiveCard] = useState<string | null>(null);
  const handleSignClick = (sign: Sign) => {
    setSelectedSign(sign);
    setTimeout(() => {
      navigate(`/${sign.id}`);
    }, 1300);
  };
  return <section id="sign-grid" className="relative py-16 sm:py-20 px-4 sm:px-6 bg-cosmic-black" data-id="element-159">
      <div className="container mx-auto max-w-7xl" data-id="element-160">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-center text-white mb-3 sm:mb-4" data-id="element-161">
          Los Doce Reinos
        </motion.h2>
        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="text-center text-gold-light/70 mb-12 sm:mb-16 italic text-sm sm:text-base" data-id="element-162">
          Toca una entidad para entrar a su reino.
        </motion.p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6" data-id="element-163">
          {signs.map((sign, index) => {
          const isActive = activeCard === sign.id;
          return <motion.div key={sign.id} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: Math.min(index * 0.05, 0.3)
          }} onClick={() => handleSignClick(sign)} onMouseEnter={() => setActiveCard(sign.id)} onMouseLeave={() => setActiveCard(null)} onTouchStart={() => setActiveCard(sign.id)} className="relative group cursor-pointer" data-id="element-164">
                <div className="relative overflow-hidden rounded-xl border-2 border-opacity-30 transition-all duration-500 group-hover:border-opacity-100" style={{
              borderColor: sign.colors.primary,
              boxShadow: isActive ? `0 0 40px ${sign.colors.primary}70, inset 0 0 60px ${sign.colors.primary}30` : `0 0 15px ${sign.colors.primary}20`
            }} data-id="element-165">
                  <div className="relative h-[24rem] sm:h-[28rem] overflow-hidden bg-cosmic-darker" data-id="element-166">
                    <img src={sign.imageUrl} alt={sign.entidad} className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105" style={{
                  filter: isActive ? 'brightness(1.15) contrast(1.08) saturate(1.15)' : 'brightness(0.85)'
                }} loading="lazy" decoding="async" data-id="element-167" />

                    <motion.div className="absolute inset-0 pointer-events-none mix-blend-color" initial={{
                  opacity: 0
                }} animate={{
                  opacity: isActive ? 0.35 : 0
                }} transition={{
                  duration: 0.4
                }} style={{
                  backgroundColor: sign.colors.primary
                }} data-id="element-168" />

                    <motion.div className="absolute inset-0 pointer-events-none" initial={{
                  opacity: 0
                }} animate={{
                  opacity: isActive ? 1 : 0
                }} transition={{
                  duration: 0.5
                }} style={{
                  boxShadow: `inset 0 0 100px ${sign.colors.primary}80`
                }} data-id="element-169" />

                    <div className="absolute inset-0 opacity-70 group-hover:opacity-50 transition-opacity duration-500 pointer-events-none" style={{
                  background: `linear-gradient(to top, #050508 0%, ${sign.colors.primary}20 40%, transparent 70%)`
                }} data-id="element-170" />

                    {/* Particles only on hover/active to save resources */}
                    {isActive && <div className="absolute inset-0 pointer-events-none" data-id="element-171">
                        <ParticleField type={sign.particleType} color={sign.colors.primary} density={12} data-id="element-172" />
                      </div>}
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 pointer-events-none" data-id="element-173">
                    <div className="flex items-center justify-between mb-2" data-id="element-174">
                      <h3 className="text-xl sm:text-2xl font-serif font-bold text-white" style={{
                    textShadow: '0 2px 12px rgba(0,0,0,0.9)'
                  }} data-id="element-175">
                        {sign.name}
                      </h3>
                      <span className="text-3xl sm:text-4xl opacity-70 group-hover:opacity-100 transition-opacity" style={{
                    color: sign.colors.primary,
                    textShadow: `0 0 20px ${sign.colors.primary}`
                  }} data-id="element-176">
                        {sign.symbol}
                      </span>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-200 mb-2" style={{
                  textShadow: '0 1px 8px rgba(0,0,0,0.9)'
                }} data-id="element-177">
                      {sign.arquetipo}
                    </p>

                    <motion.p initial={{
                  opacity: 0,
                  height: 0
                }} animate={isActive ? {
                  opacity: 1,
                  height: 'auto'
                } : {
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.3
                }} className="text-xs italic mt-2 overflow-hidden" style={{
                  color: sign.colors.accent,
                  textShadow: '0 1px 8px rgba(0,0,0,0.9)'
                }} data-id="element-178">
                      {sign.phrase}
                    </motion.p>
                  </div>
                </div>
              </motion.div>;
        })}
        </div>
      </div>

      {/* Portal transition overlay — image zoom only, no GIF */}
      <AnimatePresence data-id="element-179">
        {selectedSign && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] pointer-events-none overflow-hidden" data-id="element-180">
            <motion.div initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 3,
          opacity: 1
        }} transition={{
          duration: 1.2,
          ease: 'easeInOut'
        }} className="absolute inset-0" style={{
          background: `radial-gradient(circle, ${selectedSign.colors.primary} 0%, ${selectedSign.colors.secondary} 50%, #050508 100%)`
        }} data-id="element-181" />

            <ParticleField type={selectedSign.particleType} color={selectedSign.colors.primary} density={50} data-id="element-182" />

            <motion.div initial={{
          scale: 0.3,
          opacity: 0
        }} animate={{
          scale: [0.3, 1, 1.8],
          opacity: [0, 1, 0]
        }} transition={{
          duration: 1.3,
          ease: 'easeOut',
          times: [0, 0.5, 1]
        }} className="absolute inset-0 flex items-center justify-center" data-id="element-183">
              <div className="relative w-[80vw] max-w-2xl aspect-[3/4] rounded-2xl overflow-hidden" style={{
            boxShadow: `0 0 100px ${selectedSign.colors.primary}, 0 0 200px ${selectedSign.colors.primary}80`
          }} data-id="element-184">
                <img src={selectedSign.imageUrl} alt={selectedSign.entidad} className="w-full h-full object-cover" decoding="async" data-id="element-185" />
                <div className="absolute inset-0" style={{
              background: `radial-gradient(circle at center, transparent 30%, ${selectedSign.colors.primary}60 100%)`
            }} data-id="element-186" />
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}